import { apiRequest } from '@/services/http'

/**
 * `bs-talent.profiles`, as `talent-service`'s `GET /v1/profile` returns it.
 *
 * `education.degreeName`/`degreeType` and `languages.languageName` are meant to be resolved
 * LABELS, each paired with its own elId field (`degreeNameId`/`degreeTypeId`; `language` doubles
 * as id and lookup key) — but the migrated rows have a real bug: the raw elId landed in the label
 * field instead, and the id field was left empty. `profile-page.tsx`'s `resolveMaybeElId`
 * rescues that for display; this client just carries whatever the record holds.
 */
export interface TalentProfile {
  _id?: string
  slug?: string
  aboutMe?: string
  skills: Array<{ skillId?: string; skillName?: string }>
  employment: Array<{
    companyName?: string
    companyId?: string
    titleId?: number
    titleName?: string
    companyLogoUrl?: string
    from?: string
    to?: string
    description?: string
  }>
  education: Array<{
    degreeNameId?: number
    // `string` per the schema's OWN declared type — but `GET /v1/profile` reads via `.lean()`,
    // which skips Mongoose's casting, so a pre-existing bad row (see the module comment) comes
    // back holding the raw `number` its BSON actually is. Typed honestly so a reader can't
    // assume `.trim()` exists, the way `resolveMaybeElId` originally did before this crashed on
    // exactly this record.
    degreeName?: string | number
    degreeTypeId?: number
    degreeType?: string | number
    educationInstitution?: string
    educationInstitutionId?: string
    to?: string
  }>
  languages: Array<{ language: number; languageName?: string; level: number }>
  /** `htmlFileName` is legacy-only (CloudConvert preview, never generated by this rebuild — see
   * resumes.controller.ts) — present on migrated rows, absent on anything uploaded here. */
  cvs: Array<{ fileName?: string; htmlFileName?: string; displayName?: string; isSelected?: boolean; uploadedAt?: string }>
  links?: { linkedin?: string; facebook?: string; twitter?: string; website?: string; github?: string }
}

export interface UpdateTalentProfileInput {
  aboutMe?: string
  skills?: Array<{ skillId?: string; skillName: string }>
  employment?: Array<{
    companyName: string
    companyId?: string
    titleName?: string
    titleId?: number
    from?: string
    to?: string
    description?: string
  }>
  education?: Array<{
    degreeNameId?: number
    degreeName?: string
    degreeTypeId?: number
    degreeType?: string
    educationInstitution: string
    educationInstitutionId?: string
    to?: string
  }>
  languages?: Array<{ language: number; languageName?: string; level: number }>
  links?: { linkedin?: string; facebook?: string; twitter?: string; website?: string; github?: string }
}

export function getMyProfile(): Promise<{ profile: TalentProfile }> {
  return apiRequest('/v1/profile')
}

export function updateMyProfile(input: UpdateTalentProfileInput): Promise<{ profile: TalentProfile }> {
  return apiRequest('/v1/profile', { method: 'PATCH', body: JSON.stringify(input) })
}
